/** Show calendar dates and clocks are interpreted in Adelaide local time. */
export const SHOW_TIMEZONE = "Australia/Adelaide";
export const SHOW_DATE_LOCALE = "en-AU";

const WEEKDAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Convert a CMS/GraphQL datetime into a local calendar Date (Y/M/D only)
 * for Australia/Adelaide.
 *
 * Statamic often returns UTC wall-clock without a timezone suffix, e.g.
 * `"2026-09-04 14:30:00"` for midnight 5 Sep Adelaide. Parsing that string
 * as local time shifts the show day filter a day early.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseShowCalendarDate(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return calendarDateInTimeZone(value, SHOW_TIMEZONE);
  }

  const str = String(value).trim();
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    return new Date(year, month - 1, day);
  }

  const hasTz = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(str);
  const normalized = str.includes("T") ? str : str.replace(" ", "T");
  const instant = new Date(hasTz ? normalized : `${normalized}Z`);
  if (Number.isNaN(instant.getTime())) return null;

  return calendarDateInTimeZone(instant, SHOW_TIMEZONE);
}

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {Date|null}
 */
function calendarDateInTimeZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const day = Number(parts.find((part) => part.type === "day")?.value);

  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/**
 * Current show calendar day in Adelaide (date-only, no time component).
 * @param {boolean} [testMode]
 * @param {string|Date|null|undefined} [testDate]
 * @returns {Date}
 */
export function getShowToday(testMode = false, testDate = null) {
  if (testMode && testDate) {
    const parsed = parseShowCalendarDate(testDate);
    if (parsed) return parsed;
  }
  return calendarDateInTimeZone(new Date(), SHOW_TIMEZONE);
}

/**
 * Adelaide wall-clock parts for a given instant (default: now).
 * @param {Date} [instant]
 */
export function getShowDateTimeParts(instant = new Date()) {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: SHOW_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  }).formatToParts(instant);

  const read = (type) =>
    Number(parts.find((part) => part.type === type)?.value ?? NaN);

  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

/**
 * Date object whose calendar fields match Adelaide local time (for legacy APIs
 * that read getHours/getDate on a "today" value).
 * @param {boolean} [testMode]
 * @param {string|Date|null|undefined} [testDate]
 * @returns {Date}
 */
export function getShowNow(testMode = false, testDate = null) {
  const instant = testMode && testDate ? new Date(testDate) : new Date();
  const parts = getShowDateTimeParts(instant);
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
}

/**
 * Convert an Adelaide local date/time to a UTC instant.
 * @param {number} year
 * @param {number} month 1–12
 * @param {number} day
 * @param {number} [hour]
 * @param {number} [minute]
 * @param {number} [second]
 * @returns {Date}
 */
export function showLocalDateTimeToInstant(
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0
) {
  let utc = Date.UTC(year, month - 1, day, hour, minute, second);

  for (let i = 0; i < 3; i++) {
    const offset = getTimeZoneOffsetMs(new Date(utc), SHOW_TIMEZONE);
    utc = Date.UTC(year, month - 1, day, hour, minute, second) - offset;
  }

  return new Date(utc);
}

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {number}
 */
function getTimeZoneOffsetMs(date, timeZone) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return asUtc - date.getTime();
}

/**
 * @param {string|Date|null|undefined} a
 * @param {string|Date|null|undefined} b
 */
export function sameShowCalendarDay(a, b) {
  const dateA = parseShowCalendarDate(a);
  const dateB = parseShowCalendarDate(b);
  if (!dateA || !dateB) return false;
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

/** @param {number} day */
export function getOrdinalSuffix(day) {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/** e.g. "5 Sep" for What's On cards — Adelaide calendar day */
export function formatShowCalendarDayLabel(value) {
  const date = parseShowCalendarDate(value);
  if (!date) return "";
  const day = date.getDate();
  const month = MONTH_SHORT[date.getMonth()];
  return `${day} ${month}`;
}

/** e.g. `{ day: "SAT", date: "5th Sep" }` for the day filter bar */
export function formatShowCalendarDayFilterLabel(calendarDate) {
  const date = parseShowCalendarDate(calendarDate);
  if (!date) return { day: "", date: "" };

  const day = WEEKDAY_SHORT[date.getDay()];
  const dateNum = date.getDate();
  const month = MONTH_SHORT[date.getMonth()];

  return {
    day,
    date: `${dateNum}${getOrdinalSuffix(dateNum)} ${month}`,
  };
}

/** Value object for day filter state / URL slugs */
export function formatShowCalendarDayFilterValue(calendarDate) {
  const date = parseShowCalendarDate(calendarDate);
  if (!date) return { slug: "", date: null, forecast: [] };

  const dayAbbreviation = WEEKDAY_SHORT[date.getDay()];
  const dateNum = date.getDate();
  const monthAbbreviation = MONTH_SHORT[date.getMonth()];

  return {
    slug: `${dayAbbreviation}-${dateNum}-${monthAbbreviation}`,
    date,
    forecast: [],
  };
}

/** YYYY-MM-DD from a show calendar date */
export function formatShowCalendarIso(calendarDate) {
  const date = parseShowCalendarDate(calendarDate);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const DATE_RANGE_SEPARATOR = " — ";

/**
 * Human-readable show date range for hero / countdown banners.
 * @param {string|null|undefined} unformattedStartDate
 * @param {string|null|undefined} unformattedEndDate
 */
export function formatShowDateRangeLabel(
  unformattedStartDate,
  unformattedEndDate
) {
  if (!unformattedStartDate || !unformattedEndDate) return "";

  const start = parseShowCalendarDate(unformattedStartDate);
  const end = parseShowCalendarDate(unformattedEndDate);
  if (!start || !end) return "";

  const sameMonthAndYear =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  if (sameMonthAndYear) {
    const dayStart = start.getDate();
    const dayEnd = end.getDate();
    const monthAndYear = start.toLocaleDateString(SHOW_DATE_LOCALE, {
      month: "long",
      year: "numeric",
    });
    return `${dayStart}${DATE_RANGE_SEPARATOR}${dayEnd} ${monthAndYear}`;
  }

  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString(SHOW_DATE_LOCALE, {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}${DATE_RANGE_SEPARATOR}${end.toLocaleDateString(SHOW_DATE_LOCALE, {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}`;
  }

  return `${start.toLocaleDateString(SHOW_DATE_LOCALE, {
    day: "numeric",
    month: "long",
  })}${DATE_RANGE_SEPARATOR}${end.toLocaleDateString(SHOW_DATE_LOCALE, {
    day: "numeric",
    month: "long",
  })} ${end.getFullYear()}`;
}

/**
 * Countdown until show gates open in Adelaide local time.
 * @param {string|Date|null|undefined} unformattedStartDate
 * @param {number} [startTimeHour]
 */
export function getShowDynamicCountdown(unformattedStartDate, startTimeHour = 0) {
  const calendarDay = parseShowCalendarDate(unformattedStartDate);
  if (!calendarDay) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isWithin100: false,
    };
  }

  const targetDate = showLocalDateTimeToInstant(
    calendarDay.getFullYear(),
    calendarDay.getMonth() + 1,
    calendarDay.getDate(),
    startTimeHour,
    0,
    0
  );

  const timeUntil = targetDate.getTime() - Date.now();
  const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeUntil % (1000 * 60)) / 1000);
  const isWithin100 = days < 100 && days >= 0;

  return { days, hours, minutes, seconds, isWithin100 };
}

/**
 * Display label for an event start/end time.
 * Accepts CMS `HH:MM` / `HH:MM:SS`, already-formatted 12h strings, boolean
 * `true` (all-day sentinel from getDatesEventIsOn), or null/missing.
 *
 * @param {string|boolean|null|undefined} time
 * @param {{ allDay?: boolean, empty?: string }} [options]
 * @returns {string}
 */
export function formatEventTimeLabel(time, options = {}) {
  const empty = options.empty ?? "-";
  if (options.allDay || time === true) return "All day";
  if (time == null || time === "") return empty;
  if (typeof time !== "string") return empty;

  const trimmed = time.trim();
  if (!trimmed) return empty;

  // Already human-readable (e.g. "9:00 AM")
  if (/[ap]m$/i.test(trimmed)) return trimmed;

  const [hourPart, minutePart] = trimmed.split(":");
  const hour = Number.parseInt(hourPart, 10);
  const minute = (minutePart ?? "00").slice(0, 2);
  if (
    Number.isNaN(hour) ||
    hour < 0 ||
    hour > 23 ||
    !/^\d{1,2}$/.test(minute)
  ) {
    return empty;
  }

  const hour12 = hour % 12 || 12;
  const period = hour < 12 ? "AM" : "PM";
  return `${hour12}:${minute.padStart(2, "0")} ${period}`;
}

/**
 * Parse Y/M/D and optional H:M:S from a CMS datetime string as Adelaide local
 * wall-clock components (what the client enters in Statamic).
 * @param {string|Date|null|undefined} value
 */
export function parseCmsAdelaideDateTimeParts(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const parts = getShowDateTimeParts(value);
    return {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour,
      minute: parts.minute,
      second: parts.second,
    };
  }

  const str = String(value).trim();
  const match = str.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? 0),
    minute: Number(match[5] ?? 0),
    second: Number(match[6] ?? 0),
  };
}

/**
 * Parse a CMS/GraphQL datetime to a UTC instant.
 *
 * Statamic GraphQL returns UTC wall-clock without a timezone suffix, e.g.
 * `"2026-08-07 07:30:00"` for 5:00 PM on 7 Aug in Adelaide. The CP stores
 * Adelaide local times, but the API serializes them as UTC.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseShowDateTimeInstant(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const str = String(value).trim();
  const hasTz = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(str);
  const normalized = str.includes("T") ? str : str.replace(" ", "T");
  const instant = new Date(hasTz ? normalized : `${normalized}Z`);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/** Adelaide calendar date for competition copy labels from a CMS datetime. */
export function parseCmsAdelaideCalendarDate(value) {
  return parseShowCalendarDate(value);
}

/**
 * Format a CMS datetime for display in Adelaide.
 * @param {string|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 */
export function formatShowDateTimeLabel(value, options = {}) {
  if (value == null || value === "") return "";

  const str = String(value).trim();
  const hasTz = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(str);
  const normalized = str.includes("T") ? str : str.replace(" ", "T");
  const instant = new Date(hasTz ? normalized : `${normalized}Z`);
  if (Number.isNaN(instant.getTime())) return "";

  return instant.toLocaleDateString(SHOW_DATE_LOCALE, {
    timeZone: SHOW_TIMEZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options,
  });
}

/** Live clock string parts for What's On — weekday, date with ordinal, month */
export function formatShowLiveClockParts(instant = new Date()) {
  const parts = getShowDateTimeParts(instant);
  const weekday = new Intl.DateTimeFormat(SHOW_DATE_LOCALE, {
    timeZone: SHOW_TIMEZONE,
    weekday: "long",
  }).format(instant);
  const month = new Intl.DateTimeFormat(SHOW_DATE_LOCALE, {
    timeZone: SHOW_TIMEZONE,
    month: "long",
  }).format(instant);
  const hour12 = parts.hour % 12 || 12;
  const amPm = parts.hour < 12 ? "am" : "pm";

  return {
    hour: hour12,
    minute: String(parts.minute).padStart(2, "0"),
    amPm,
    weekday,
    day: parts.day,
    ordinal: getOrdinalSuffix(parts.day),
    month,
  };
}
